type UnknownRecord = Record<string, unknown>;

export type FleetMaintenanceServiceEntryLike = {
  next_service_date?: string | null;
};

export type FleetServiceHealth = 'due_now' | 'watch' | 'healthy';

export interface FleetMaintenanceStatusOptions {
  vehicleCustomFields?: UnknownRecord | null;
  serviceEntries?: FleetMaintenanceServiceEntryLike[];
  currentOdometerKm?: number | null;
  liveOdometerMeters?: number | null;
  serviceWarningKm?: number;
}

export interface FleetMaintenanceStatus {
  currentOdometerKm: number | null;
  nextServiceOdometerKm: number | null;
  serviceDeltaKm: number | null;
  serviceHealth: FleetServiceHealth | null;
  serviceLabel: string | null;
  nextServiceDate: string | undefined;
  isServiceOverdue: boolean;
  isServiceDueSoon: boolean;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function formatIsoDateOnly(value: Date): string {
  return value.toISOString().split('T')[0];
}

export function getFleetMaintenanceDueDate(
  serviceEntries: FleetMaintenanceServiceEntryLike[] = [],
  vehicleCustomFields?: UnknownRecord | null,
): string | undefined {
  const times: number[] = [];
  const rawVehicleDate = vehicleCustomFields?.next_service_date;

  if (typeof rawVehicleDate === 'string' && rawVehicleDate.trim()) {
    const parsed = Date.parse(rawVehicleDate);
    if (Number.isFinite(parsed)) {
      times.push(parsed);
    }
  }

  for (const entry of serviceEntries) {
    if (typeof entry?.next_service_date !== 'string' || !entry.next_service_date.trim()) {
      continue;
    }

    const parsed = Date.parse(entry.next_service_date);
    if (Number.isFinite(parsed)) {
      times.push(parsed);
    }
  }

  if (times.length === 0) {
    return undefined;
  }

  const earliest = new Date(Math.min(...times));
  if (Number.isNaN(earliest.getTime())) {
    return undefined;
  }

  return formatIsoDateOnly(earliest);
}

export function getFleetServiceHealth(
  serviceDeltaKm: number | null,
  serviceWarningKm: number = 1000,
): FleetServiceHealth | null {
  if (serviceDeltaKm == null) {
    return null;
  }

  if (serviceDeltaKm <= 0) {
    return 'due_now';
  }

  if (serviceDeltaKm <= serviceWarningKm) {
    return 'watch';
  }

  return 'healthy';
}

export function getFleetServiceDistanceLabel(serviceDeltaKm: number | null): string | null {
  if (serviceDeltaKm == null) {
    return null;
  }

  return serviceDeltaKm <= 0
    ? `${Math.abs(serviceDeltaKm).toLocaleString('en-ZA')} km overdue`
    : `${serviceDeltaKm.toLocaleString('en-ZA')} km to service`;
}

export function getFleetMaintenanceStatus({
  vehicleCustomFields,
  serviceEntries = [],
  currentOdometerKm,
  liveOdometerMeters,
  serviceWarningKm = 1000,
}: FleetMaintenanceStatusOptions): FleetMaintenanceStatus {
  const resolvedCurrentOdometerKm = toFiniteNumber(currentOdometerKm)
    ?? (() => {
      const liveMeters = toFiniteNumber(liveOdometerMeters);
      return liveMeters != null ? liveMeters / 1000 : null;
    })()
    ?? toFiniteNumber(vehicleCustomFields?.odometer);

  const nextServiceOdometerKm = toFiniteNumber(vehicleCustomFields?.next_service_odometer);
  const serviceDeltaKm = resolvedCurrentOdometerKm != null && nextServiceOdometerKm != null
    ? Math.round(nextServiceOdometerKm - resolvedCurrentOdometerKm)
    : null;
  const serviceHealth = getFleetServiceHealth(serviceDeltaKm, serviceWarningKm);

  return {
    currentOdometerKm: resolvedCurrentOdometerKm,
    nextServiceOdometerKm,
    serviceDeltaKm,
    serviceHealth,
    serviceLabel: getFleetServiceDistanceLabel(serviceDeltaKm),
    nextServiceDate: getFleetMaintenanceDueDate(serviceEntries, vehicleCustomFields),
    isServiceOverdue: serviceDeltaKm != null && serviceDeltaKm <= 0,
    isServiceDueSoon: serviceDeltaKm != null && serviceDeltaKm > 0 && serviceDeltaKm <= serviceWarningKm,
  };
}